import React, { useMemo } from "react";
import { formatEntryThumbnailUrl, HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { Modal, ModalContent, ModalCloseButton, modalContentClasses } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import { translate, translateTime } from "@kaltura/mediaspace-shared-utils";
import { StyledThumbnail, useTheme } from "@kaltura/mediaspace-shared-styled";
import { generalHtmlStyles } from "@kaltura/mediaspace-shared-ui";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { TypographyProps, Typography } from "@kaltura/ds-react-components";
import { uniqueId } from "lodash";

export interface NewsItemModalProps {
    onHide: () => void;
    show: boolean;
    name: string;
    description: string;
    thumbnailUrl: string;
    startDate: number;
    endDate?: number;
    modalBoxMaskColor?: string;
    imageAltText?: string;
}

const StyledModal = styled(Modal)(({ theme, fullScreen }) => [
    !fullScreen && {
        ".kms-ds-homepage-component-news-modal-paper": {
            width: 994,
            [theme.breakpoints.down("lg")]: {
                width: 798,
            },
        },
    },
]);

const StyledImage = styled(StyledThumbnail)(({ theme }) => ({
    float: "left",
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(2),
    minWidth: 133,
    maxWidth: 133,
    height: "auto",
    borderRadius: theme.kaltura.shape.roundness2,
    [theme.breakpoints.up("sm")]: {
        minWidth: 280,
        maxWidth: 280,
    },
    [theme.breakpoints.up("lg")]: {
        minWidth: 320,
        maxWidth: 320,
    },
}));
const StyledUpdatedAt = styled("div")(({ theme }) => ({
    fontSize: theme.typography.pxToRem(14),
    textTransform: "capitalize",
    color: theme.kaltura.palette.tone2,
    lineHeight: 1.38,
    marginBottom: theme.spacing(1),
}));

// lock the component to h3, regardless of variant
// the intermediate component is to prevent ts from complaining of the missing `component` prop
const NameComponent = (props: Omit<TypographyProps<"h3">, "component">) => {
    return <Typography {...props} component={"h3"} />;
};

const StyledName = styled(NameComponent)(({ theme }) => ({
    marginBottom: 24,
    color: theme.kaltura.palette.tone1,
}));

const StyledDescription = styled("div")(({ theme }) => [
    {
        display: "table",
        fontWeight: theme.kaltura.typography.fontWeightRegular,
        fontSize: theme.typography.pxToRem(16),
        lineHeight: theme.typography.pxToRem(21),
        color: theme.kaltura.palette.tone2,
        [theme.breakpoints.down("sm")]: {
            clear: "both",
        },
    },
    generalHtmlStyles(theme),
]);

const StyledModalContent = styled(ModalContent)(({ theme }) => ({
    [`.${modalContentClasses.root}&`]: {
        padding: theme.spacing(5),
    },
    [theme.breakpoints.down("md")]: {
        [`.${modalContentClasses.root}&`]: {
            padding: theme.spacing(3),
        },
    },
}));

/**
 * NewsItemModal Component displays news entry modal
 */
export const NewsItemModal: React.FC<NewsItemModalProps> = (props: NewsItemModalProps) => {
    const { show, onHide, name, description, thumbnailUrl, startDate, endDate, modalBoxMaskColor, imageAltText } =
        props;

    const titleElementId = useMemo(() => {
        return uniqueId("item-title_");
    }, []);

    const context = useConfig();
    const { dateFormats } = context.application;

    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const xsScreen = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <StyledModal
            aria-labelledby={titleElementId}
            variant={"content"}
            backdropColor={modalBoxMaskColor}
            open={show}
            onClose={onHide}
            maxWidth={false}
            fullScreen={smallScreen}
            classes={{
                paper: "kms-ds-homepage-component-news-modal-paper",
            }}
        >
            <ModalCloseButton
                onClick={onHide}
                aria-label={translate("close")}
                classes={{ closeIcon: "kms-ds-homepage-component-news-modal-close" }}
            />
            <StyledModalContent className="kms-ds-news-item-modal-content">
                <StyledImage
                    src={formatEntryThumbnailUrl(thumbnailUrl, 320, 400)}
                    alt={imageAltText || translate("News Image")}
                />
                <StyledUpdatedAt className="kms-ds-news-item-modal-date">
                    {translateTime(startDate, dateFormats.shortDate)}
                    {endDate ? " - " + translateTime(endDate, dateFormats.shortDate) : ""}
                </StyledUpdatedAt>
                <StyledName
                    id={titleElementId}
                    variant={!xsScreen ? "h2" : "h3"}
                    className="kms-ds-news-item-modal-title"
                >
                    {name}
                </StyledName>
                <StyledDescription className="kms-ds-news-item-modal-description">
                    {HtmlReactParser(description)}
                </StyledDescription>
            </StyledModalContent>
        </StyledModal>
    );
};

export default NewsItemModal;
